import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { get, remove } from "../../../utils/apiUtils";

/* ---------------------------- Types ---------------------------- */
export type HighlightRow = {
  id: string;
  document_name: string;
  created_at: string; // ISO
  username: string;
  length: string; // "56 chars"
  codesCount: number;
  content?: string;
  start_position?: number;
  end_position?: number;
  code_name?: string[]; // emrat e kodeve
};

export type HighlightsState = {
  projectId: string | null;
  items: HighlightRow[];
  total: number;
  page: number;
  pageSize: number;
  username: string;
  loading: boolean;

  dirty: boolean;

  // view e një highlight-i
  selected: HighlightRow | null;
  selectedLoading: boolean;
};

const initialState: HighlightsState = {
  projectId: null,
  items: [],
  total: 0,
  page: 1,
  pageSize: 10,
  username: "",
  loading: false,
  dirty: false,
  selected: null,
  selectedLoading: false,
};

/* ---------------------------- Thunks --------------------------- */
export const fetchHighlights = createAsyncThunk(
  "highlights/fetchList",
  async (_: void, { getState }) => {
    const { highlights } = getState() as any as { highlights: HighlightsState };
    const { projectId, page, pageSize, username } = highlights;
    if (!projectId) throw new Error("Missing project id");

    const skip = (page - 1) * pageSize;

    const params = new URLSearchParams({
      project_id: projectId,
      skip: String(skip),
      limit: String(pageSize),
    });
    if (username) params.append("username", username);

    const res = await get<any>(`/highlights?${params.toString()}`, {
      headers: { Accept: "application/json" },
    });
    if (res.type !== "success") {
      throw new Error(res?.data?.message || "Failed to fetch highlights");
    }

    const src: any[] = Array.isArray(res.data?.items)
      ? res.data.items
      : Array.isArray(res.data)
      ? res.data
      : [];

    const items: HighlightRow[] = src.map((h: any) => {
      const codeNames: string[] = Array.isArray(h.codes)
        ? h.codes.map((c: any) => c.code_name).filter(Boolean)
        : [];
      const lengthChars =
        (typeof h.content === "string" ? h.content.length : h.length) || 0;

      return {
        id: h.id,
        document_name: h.document_name ?? "Unknown",
        created_at: h.created_at ?? "",
        username: h.username ?? "",
        length: `${lengthChars} chars`,
        codesCount: h.codesCount ?? h.code_count ?? codeNames.length,
      };
    });

    const total = res.data?.total ?? items.length;
    return { items, total };
  }
);

export const deleteHighlight = createAsyncThunk(
  "highlights/deleteOne",
  async (id: string) => {
    const res = await remove<any>(`/highlights/${id}`, {
      headers: { Accept: "application/json" },
    });
    if (res?.type !== "success") {
      throw new Error(res?.data?.message || "Failed to delete highlight");
    }
    return id;
  }
);

export const fetchHighlightById = createAsyncThunk(
  "highlights/fetchById",
  async (id: string) => {
    const res = await get<any>(`/highlights/${id}`, {
      headers: { Accept: "application/json" },
    });
    if (res?.type !== "success") {
      throw new Error(res?.data?.message || "Failed to fetch highlight");
    }
    const d = res.data;

    const codeNames: string[] = Array.isArray(d.codes)
      ? d.codes.map((c: any) => c.code_name).filter(Boolean)
      : [];

    const row: HighlightRow = {
      id: d.id,
      content: d.content,
      document_name: d.document_name ?? "Unknown",
      created_at: d.created_at ?? "",
      end_position: d.end_position,
      start_position: d.start_position,
      username: d.username ?? "",
      code_name: codeNames,
      codesCount: codeNames.length,
      length: `${d.content?.length || 0} chars`,
    };
    return row;
  }
);

/* ---------------------------- Slice ---------------------------- */
const highlightsSlice = createSlice({
  name: "highlights",
  initialState,
  reducers: {
    setHighlightsProjectId(state, action: PayloadAction<string | null>) {
      state.projectId = action.payload;
      state.items = [];
      state.total = 0;
      state.page = 1;
      state.username = "";
      state.selected = null;
      state.dirty = false;
    },
    setHighlightsPage(state, action: PayloadAction<number>) {
      state.page = action.payload;
    },
    setHighlightsPageSize(state, action: PayloadAction<number>) {
      state.pageSize = action.payload;
      state.page = 1;
    },
    setHighlightsUsername(state, action: PayloadAction<string>) {
      state.username = action.payload;
      state.page = 1;
    },
    clearSelectedHighlight(state) {
      state.selected = null;
    },
    markHighlightsDirty(state) {
      state.dirty = true;
    },
    clearHighlightsDirty(state) {
      state.dirty = false;
    },
  },
  extraReducers: (b) => {
    b.addCase(fetchHighlights.pending, (s) => {
      s.loading = true;
    });
    b.addCase(fetchHighlights.fulfilled, (s, a) => {
      s.loading = false;
      s.items = a.payload.items;
      s.total = a.payload.total;
    });
    b.addCase(fetchHighlights.rejected, (s) => {
      s.loading = false;
    });

    b.addCase(deleteHighlight.fulfilled, (s, a) => {
      const id = a.payload;
      s.items = s.items.filter((x) => x.id !== id);
      s.total = Math.max(0, s.total - 1);
      if (s.selected?.id === id) s.selected = null;
    });

    b.addCase(fetchHighlightById.pending, (s) => {
      s.selectedLoading = true;
    });
    b.addCase(fetchHighlightById.fulfilled, (s, a) => {
      s.selectedLoading = false;
      s.selected = a.payload;
    });
    b.addCase(fetchHighlightById.rejected, (s) => {
      s.selectedLoading = false;
    });
  },
});

export const {
  setHighlightsProjectId,
  setHighlightsPage,
  setHighlightsPageSize,
  setHighlightsUsername,
  clearSelectedHighlight,
  markHighlightsDirty,
  clearHighlightsDirty,
} = highlightsSlice.actions;

export default highlightsSlice.reducer;
